import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TableCompareTrigger } from "../../../components/lcaComparison";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  addFlow44_1LCA,
  deleteFlow44_1LCA,
  getAllFlow44_1LCA,
  updateFlow44_1LCA,
} from "../../../../../../apis/Product/WasteProcessingFlow44Api";
import {
  getProductFlow44_1ProcessedDropdown,
  getProductFlow44_1NextWasteDropdown,
} from "../../../../../../apis/Dropdown";

// Impact categories configuration (16)
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  { name: "Eutrophication, marine", unit: "kg N-eq", key: "eutrophication_marine" },
  { name: "Human toxicity, cancer effects", unit: "CTUh", key: "carcinogenics" },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  { name: "Particulate matter", unit: "disease incidence", key: "respiratory_effects" },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  { name: "Resource use, energy carriers", unit: "MJ", key: "fossil_fuel_depletion" },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const Flow44_1Table = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] = useState(0);
  const [processedDropdown, setProcessedDropdown] = useState([]);
  const [nextWasteDropdown, setNextWasteDropdown] = useState([]);
  const [unitProcessed, setUnitProcessed] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductFlow44_1ProcessedDropdown({
      setDropdown: setProcessedDropdown,
    });
    getProductFlow44_1NextWasteDropdown({
      setDropdown: setNextWasteDropdown,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    if (!productId) return;
    getAllFlow44_1LCA({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && processedDropdown.length > 0) {
      const newUnits = {};
      data.forEach((row) => {
        const processedCode =
          typeof row.how_is_part_processed === "string"
            ? row.how_is_part_processed
            : row.how_is_part_processed?.value || row.how_is_part_processed?.code;
        if (processedCode) {
          const selected = processedDropdown.find(
            (opt) =>
              !opt.isCategory &&
              (opt.code === processedCode || opt.value === processedCode)
          );
          if (selected) {
            newUnits[row.id] = selected.unit || "xx";
          }
        }
      });
      setUnitProcessed(newUnits);
    }
  }, [data, processedDropdown]);

  const dynamicPlaceholders = useMemo(
    () => ({
      how_is_part_processed: {
        targetField: "quantity_per_fu",
        getPlaceholder: (selectedValue, dropdownData, _rowData, rowId = "new") => {
          const selected = dropdownData?.find(
            (opt) => opt?.value === selectedValue || opt?.code === selectedValue
          );
          if (selected) {
            setUnitProcessed((prev) => ({
              ...prev,
              [rowId]: selected.unit || "xx",
            }));
          }
        },
        dropdownDataKey: "how_is_part_processed",
      },
    }),
    []
  );

  const handleEdit = useCallback(
    (editedItem) => {
      const processedCode =
        typeof editedItem.how_is_part_processed === "string"
          ? editedItem.how_is_part_processed
          : editedItem.how_is_part_processed?.value || 
            editedItem.how_is_part_processed?.code || 
            null;

      const nextWasteCode =
        typeof editedItem.next_waste_process_scenario === "string"
          ? editedItem.next_waste_process_scenario
          : editedItem.next_waste_process_scenario?.value || 
            editedItem.next_waste_process_scenario?.code || 
            null;

      updateFlow44_1LCA({
        productId,
        rowId: editedItem.id,
        name_part_in_fu: editedItem.name_part_in_fu || null,
        describe_process: editedItem.describe_process || null,
        how_is_part_processed: processedCode,
        quantity_per_fu: editedItem.quantity_per_fu || 0,
        next_waste_process_scenario: nextWasteCode,
        collecting_scenario: editedItem.collecting_scenario || null,
        surplus_material_replaces_new_purchase: editedItem.surplus_material_replaces_new_purchase || false,
        objects: data,
        setObjects: setData,
        use_swal: true,
        callback: () => {
          window.dispatchEvent(new Event('refresh-waste-processing-44-totals'));
        },
      });
    },
    [productId, data]
  );

  const handleAdd = useCallback(
    (newItem) => {
      const processedCode =
        typeof newItem.how_is_part_processed === "string"
          ? newItem.how_is_part_processed
          : newItem.how_is_part_processed?.value || 
            newItem.how_is_part_processed?.code || 
            null;

      const nextWasteCode =
        typeof newItem.next_waste_process_scenario === "string"
          ? newItem.next_waste_process_scenario
          : newItem.next_waste_process_scenario?.value || 
            newItem.next_waste_process_scenario?.code || 
            null;

      addFlow44_1LCA({
        productId,
        name_part_in_fu: newItem.name_part_in_fu || null,
        describe_process: newItem.describe_process || null,
        how_is_part_processed: processedCode,
        quantity_per_fu: newItem.quantity_per_fu || 0,
        next_waste_process_scenario: nextWasteCode,
        collecting_scenario: newItem.collecting_scenario || null,
        surplus_material_replaces_new_purchase: newItem.surplus_material_replaces_new_purchase || false,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-waste-processing-44-totals'));
        },
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteFlow44_1LCA({
        productId,
        rowId: itemToDelete.id,
        callback: () => {
          fetchData();
          window.dispatchEvent(new Event('refresh-waste-processing-44-totals'));
        },
      });
    },
    [productId, fetchData]
  );

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
  const impactCategoryAccessor = currentImpactCategory.key;

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name_part_in_fu",
        accessor: "name_part_in_fu",
        header: t("NAME_PART_IN_FU") || "Name the part in the FU *",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
      },
      {
        id: "describe_process",
        accessor: "describe_process",
        header: t("DESCRIBE_PROCESS") || "Describe the process",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "how_is_part_processed",
        accessor: "how_is_part_processed",
        header: `${t("HOW_IS_PART_PROCESSED") || "How is the part processed"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_PROCESS") || "Select process...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "how_is_part_processed",
            processedDropdown,
            unitProcessed,
            rowId
          ),
      },
      {
        id: "quantity_per_fu",
        accessor: "quantity_per_fu",
        header: `${t("QUANTITY_PER_FU") || "Quantity per functional unit"} *`,
        type: "number",
        placeholder: t("USER_TYPES") || "User Types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "how_is_part_processed",
            processedDropdown,
            unitProcessed,
            rowId
          ),
      },
      {
        id: "next_waste_process_scenario",
        accessor: "next_waste_process_scenario",
        header: `${t("NEXT_WASTE_PROCESS_SCENARIO") || "Next waste process scenario"} *`,
        type: "dropdown",
        isCategory: true,
        placeholder: t("SELECT_NEXT_WASTE") || "Select next waste process...",
      },
      {
        id: "collecting_scenario",
        accessor: "collecting_scenario",
        header: t("COLLECTING_SCENARIO") || "Collecting scenario",
        type: "text",
        placeholder: t("USER_TYPES") || "User Types...",
        optional: true,
      },
      {
        id: "surplus_material_replaces_new_purchase",
        accessor: "surplus_material_replaces_new_purchase",
        header: t("SURPLUS_MATERIAL_REPLACES_NEW_PURCHASE") || "Does the surplus material replace a new purchase?",
        type: "checkbox",
        optional: true,
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      t,
      unitProcessed,
      processedDropdown,
      data,
      currentImpactCategory,
      currentImpactCategoryIndex,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
    ]
  );

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true,
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: { label: `Total ${currentImpactCategory.name}` },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      how_is_part_processed: processedDropdown || [],
      next_waste_process_scenario: nextWasteDropdown || [],
    }),
    [processedDropdown, nextWasteDropdown]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={useMemo(
        () => ({
          [impactCategoryAccessor]: CustomInputComponent,
        }),
        [impactCategoryAccessor]
      )}
      comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default Flow44_1Table;

